import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import {
  useModelShow,
  useModelCreate,
  useHasRoleIn,
  useBaseOwnerId
} from '@rhino-project/core/hooks';
import { networkApiCallOnlyData } from '@rhino-project/core/lib';
import { formatMoney } from '../lib/format';
import type { GuestRow } from '../lib/harborlightTypes';

// Ported 1:1 from the source repository (client/src/pages/BookRoomPage.jsx).
// Data via Rhino: `room` model show, custom /api/users/guests, reservation
// model create.

function tonight() {
  return new Date().toISOString().slice(0, 10);
}

function addDays(dateStr: string, days: number) {
  const d = new Date(dateStr);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
}

const BookRoomPage = () => {
  const { owner, id } = useParams({ strict: false }) as {
    owner: string;
    id: string;
  };
  const navigate = useNavigate();
  const isStaff = useHasRoleIn(['staff', 'admin']);
  const baseOwnerId = useBaseOwnerId();

  const { resource: room } = useModelShow('room', id);

  const [guestsList, setGuestsList] = useState<GuestRow[]>([]);
  const [checkIn, setCheckIn] = useState(tonight());
  const [checkOut, setCheckOut] = useState(addDays(tonight(), 2));
  const [guests, setGuests] = useState('1');
  const [notes, setNotes] = useState('');
  const [guestId, setGuestId] = useState<string>('');
  const [error, setError] = useState('');

  // Staff can book on behalf of a registered guest.
  useEffect(() => {
    if (!isStaff) return;
    networkApiCallOnlyData('/api/users/guests', { method: 'get' })
      .then((data: { results?: GuestRow[] }) => {
        const users = data?.results ?? [];
        setGuestsList(users);
        if (users[0]) setGuestId(String(users[0].id));
      })
      .catch((err: Error) => setError(err.message));
  }, [isStaff]);

  const nights = useMemo(() => {
    const start = new Date(checkIn);
    const end = new Date(checkOut);
    const n = Math.round(
      (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)
    );
    return Math.max(1, n);
  }, [checkIn, checkOut]);

  const { mutateAsync: createReservation, isPending: busy } =
    useModelCreate('reservation');

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    try {
      const payload: Record<string, unknown> = {
        organization: baseOwnerId,
        room: Number(id),
        check_in: checkIn,
        check_out: checkOut,
        guests: Number(guests),
        notes: notes || undefined
      };
      if (isStaff && guestId) payload.user = Number(guestId);
      await createReservation(payload as never);
      navigate({ to: '/$owner/reservations', params: { owner } });
    } catch {
      setError(
        'Could not create the reservation. The room may be unavailable for these dates.'
      );
    }
  }

  if (!room && !error) {
    return (
      <div className="d-flex justify-content-center py-5">
        <div className="spinner-border text-secondary" role="status" />
      </div>
    );
  }

  return (
    <div className="fade-up">
      <Link
        className="btn btn-link text-decoration-none px-0 mb-3"
        to="/$owner/rooms"
        params={{ owner }}
      >
        ← Back to rooms
      </Link>
      <div className="row g-4">
        <div className="col-lg-5">
          <div className="surface-panel p-4 h-100">
            <p className="text-uppercase small text-secondary mb-1">
              Reservation
            </p>
            <h1 className="display-title display-6">Room {room?.number}</h1>
            <p className="text-secondary">{room?.description}</p>
            <p className="mb-1">
              <strong>{formatMoney(room?.price_per_night)}</strong> per night ·
              capacity {room?.capacity}
            </p>
            <p className="mb-0 text-secondary">
              Estimated total for {nights} night{nights > 1 ? 's' : ''}:{' '}
              <strong className="text-dark">
                {formatMoney((room?.price_per_night || 0) * nights)}
              </strong>
            </p>
            <p className="small text-secondary mt-3 mb-0">
              Payment is collected at the hotel — no card required to book.
            </p>
          </div>
        </div>
        <div className="col-lg-7">
          <div className="surface-panel p-4">
            <h2 className="h4 display-title mb-3">Booking details</h2>
            {error && <div className="alert alert-danger">{error}</div>}
            <form className="row g-3" onSubmit={onSubmit}>
              {isStaff && (
                <div className="col-12">
                  <label className="form-label">Guest</label>
                  <select
                    className="form-select"
                    value={guestId}
                    onChange={(e) => setGuestId(e.target.value)}
                    required
                  >
                    {guestsList.map((g) => (
                      <option key={g.id} value={g.id}>
                        {g.name || g.display_name} ({g.email})
                      </option>
                    ))}
                  </select>
                </div>
              )}
              <div className="col-md-6">
                <label className="form-label">Check-in</label>
                <input
                  type="date"
                  className="form-control"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-6">
                <label className="form-label">Check-out</label>
                <input
                  type="date"
                  className="form-control"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-4">
                <label className="form-label">Guests</label>
                <input
                  type="number"
                  min={1}
                  max={room?.capacity || 10}
                  className="form-control"
                  value={guests}
                  onChange={(e) => setGuests(e.target.value)}
                  required
                />
              </div>
              <div className="col-md-8">
                <label className="form-label">Notes</label>
                <input
                  className="form-control"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Late arrival, dietary notes…"
                />
              </div>
              <div className="col-12">
                <button
                  type="submit"
                  className="btn btn-copper btn-lg"
                  disabled={busy}
                >
                  {busy ? 'Booking…' : 'Confirm booking'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BookRoomPage;
